import { supabase } from '../../../shared/lib/supabaseClient';

export const WEEK_RANGE = 'week';
export const MONTH_RANGE = 'month';

// en-CA formats as YYYY-MM-DD in the user's local time.
const isoDate = (d) => d.toLocaleDateString('en-CA');

// Every weekday (Mon-Fri) between `start` and `end` (inclusive, local time), oldest first.
function weekdaysBetween(start, end) {
  const days = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const dow = d.getDay(); // 0 Sun .. 6 Sat
    if (dow !== 0 && dow !== 6) days.push(isoDate(d));
  }
  return days;
}

// Fixed x-axis positions for `range` ('week' | 'month'), independent of whether attendance was
// actually recorded — so the range always shows in full even on a day nothing's been taken yet
// (including days still to come).
function daysForRange(range) {
  const now = new Date();
  if (range === MONTH_RANGE) {
    return weekdaysBetween(new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 0));
  }
  const dow = now.getDay(); // 0 Sun .. 6 Sat
  const monday = new Date(now);
  monday.setDate(now.getDate() + (dow === 0 ? -6 : 1 - dow));
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  return weekdaysBetween(monday, friday);
}

// Daily attendance rate (present / (present + late + absent), excused left out — the same rule
// as the rest of the app) per section the teacher teaches, over `range` ('week' = the current
// Monday-Friday, 'month' = every weekday in the current calendar month).
// `subjects` is the teacher's own subjects from getOwnSubjects(), each with .subject_sections.
// Returns { sections: [{ key, grade_level, section }], days: ['YYYY-MM-DD', ...],
// series: { [key]: (number|null)[] } }, `series[key][i]` lining up with `days[i]`, null where
// that section has no record that day.
export async function getAttendanceTrend(subjects, range = WEEK_RANGE) {
  const sectionKeys = new Map();
  subjects.forEach((s) =>
    s.subject_sections.forEach((ss) => {
      const key = `${s.grade_level}|${ss.section}`;
      if (!sectionKeys.has(key)) sectionKeys.set(key, { key, grade_level: s.grade_level, section: ss.section });
    })
  );
  if (sectionKeys.size === 0) return { sections: [], days: [], series: {} };

  const days = daysForRange(range);
  const subjectIds = subjects.map((s) => s.id);

  const { data, error } = await supabase
    .from('attendance_records')
    .select('attendance_date, status, students(grade_level, section)')
    .in('subject_id', subjectIds)
    .gte('attendance_date', days[0])
    .lte('attendance_date', days[days.length - 1]);
  if (error) throw error;

  // "date|grade|section" -> counts. Excused isn't counted, matching the attendance-rate rule
  // used elsewhere (getAdvisoryAttendance).
  const buckets = new Map();
  data.forEach((r) => {
    const student = r.students;
    if (!student?.section || r.status === 'excused') return;
    const key = `${r.attendance_date}|${student.grade_level}|${student.section}`;
    const b = buckets.get(key) ?? { present: 0, late: 0, absent: 0 };
    b[r.status] += 1;
    buckets.set(key, b);
  });

  const sections = [...sectionKeys.values()].sort(
    (a, b) => a.grade_level.localeCompare(b.grade_level) || a.section.localeCompare(b.section)
  );

  const series = {};
  sections.forEach(({ key, grade_level, section }) => {
    series[key] = days.map((day) => {
      const b = buckets.get(`${day}|${grade_level}|${section}`);
      if (!b) return null;
      const counted = b.present + b.late + b.absent;
      return counted > 0 ? (b.present / counted) * 100 : null;
    });
  });

  return { sections, days, series };
}
